// hooks/useMessages.ts — reactive hook for conversations + messages.
//
// Reads from messageStore reactively and exposes service mutations.
// Components must NOT import messageStore or messagesService directly.
//
// useShallow is used on any filtering/derived-list selector to prevent
// re-renders when unrelated conversations change.

import { useState, useEffect, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useMessageStore } from '@/core/stores/messageStore';
import { messagesService } from '@/core/services/messagesService';
import type { OutgoingMessageImage } from '@/core/services/messagesService';
import { isConnected as isRealtimeConnected } from '@/core/lib/realtime';
import type {
  Conversation,
  ConversationInviteSummary,
  Message,
  ConversationType,
} from '@/core/models/message';

export interface MessagesHook {
  /** All conversations in seed order. */
  conversations: Conversation[];
  /**
   * Total unread count across all conversations (numeric). Message requests
   * are excluded — an unaccepted thread must never light the badge.
   */
  totalUnread: number;
  /** How many threads are waiting for MY acceptance (the Requests chip count). */
  requestsCount: number;
  /** Send a message to a conversation, optionally with an already-uploaded image. */
  send: (convoId: string, body: string, image?: OutgoingMessageImage) => Promise<void>;
  /** Mark a conversation read (clears its unread count). */
  markRead: (convoId: string) => Promise<void>;
  /** Accept a message request — moves the thread into the inbox. */
  acceptRequest: (convoId: string) => Promise<Conversation>;
  /** Decline a message request — deletes the thread for BOTH participants. */
  declineRequest: (convoId: string) => Promise<void>;
  /** Start (or resolve) a DM with a handle. Throws ApiError on failure. */
  createDm: (handle: string) => Promise<Conversation>;
  /** Create a titled group with member handles. Throws ApiError on failure. */
  createGroup: (title: string, handles: string[]) => Promise<Conversation>;
  /** Invite one more handle into an existing group. Throws ApiError on failure. */
  invite: (conversationId: string, handle: string) => Promise<Conversation>;
}

export interface ConversationsByTypeHook {
  /** Conversations filtered by type. */
  conversations: Conversation[];
}

export interface ConversationMessagesHook {
  /** Messages for the selected conversation. */
  messages: Message[];
  /** Send a message to this conversation. */
  send: (body: string) => Promise<void>;
}

/** Primary hook: all conversations + send mutation. */
export function useMessages(): MessagesHook {
  // Stable store ref — no useShallow needed.
  const conversations = useMessageStore((s) => s.conversations);
  // Pending threads are requests, not inbox mail: they are excluded from the
  // badge total and counted separately for the Requests chip. The server also
  // excludes them from its own totalUnread — this keeps the two agreeing.
  const totalUnread = conversations.reduce((sum, c) => sum + (c.pending ? 0 : c.unread), 0);
  const requestsCount = conversations.reduce((n, c) => n + (c.pending ? 1 : 0), 0);

  return {
    conversations,
    totalUnread,
    requestsCount,
    send: (convoId, body, image) => messagesService.send(convoId, body, image),
    markRead: (convoId) => messagesService.markRead(convoId),
    acceptRequest: (convoId) => messagesService.accept(convoId),
    declineRequest: (convoId) => messagesService.decline(convoId),
    createDm: (handle) => messagesService.createDm(handle),
    createGroup: (title, handles) => messagesService.createGroup(title, handles),
    invite: (conversationId, handle) => messagesService.invite(conversationId, handle),
  };
}

/**
 * Live-updates the conversation store while a Messages surface is mounted:
 * an immediate refresh on mount, then a poll every `intervalMs`, paused while
 * the tab is hidden and re-fired the moment it becomes visible/focused again.
 * The realtime socket (dm.message) is the primary live-update path now; this
 * poll stays as the fallback for when it's down, so each tick checks
 * isRealtimeConnected() and skips the actual refresh() call while the socket
 * is up — a plain function check (not a reactive store subscription) because
 * tick() isn't itself a render, it only needs the current boolean at call
 * time, matching this hook's existing non-reactive style. Poll errors are
 * swallowed (a transient failure just means the next tick catches up); the
 * send-vs-poll race is guarded inside messagesService.refresh().
 */
export function useConversationPolling(intervalMs = 10_000): void {
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible' && !isRealtimeConnected()) {
        void messagesService.refresh().catch(() => {});
      }
    };
    tick();
    const interval = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [intervalMs]);
}

/**
 * Imperative "re-read the conversation list" with NO store subscription — for
 * surfaces that change something the conversation payload is computed from but
 * don't render conversations themselves (1.24.00: the read-receipts and
 * activity-status switches in Privacy & Security, whose reciprocal server-side
 * rules decide whether `otherReadAt` and `online` come back at all). Without
 * this, a stale "Seen" line or online dot survives until something else happens
 * to refetch.
 *
 * Reuses messagesService.refresh() — the same call the poll and the realtime
 * new-conversation path make, race-guard included — rather than adding a second
 * way to load conversations. Rejections are swallowed: a failed freshening must
 * not turn a saved preference into an error.
 */
export function useConversationRefresh(): () => Promise<void> {
  return useCallback(() => messagesService.refresh().catch(() => {}), []);
}

/**
 * Filtered hook: conversations for a specific type.
 * Uses useShallow because the selector returns a filtered array.
 */
export function useMessagesByType(type: ConversationType): ConversationsByTypeHook {
  const conversations = useMessageStore(
    useShallow((s) => s.conversations.filter((c) => c.type === type)),
  );
  return { conversations };
}

/**
 * Hook for a single conversation's messages.
 * Uses useShallow because the selector maps into a messages array.
 */
export function useConversationMessages(convoId: string): ConversationMessagesHook {
  const messages = useMessageStore(
    useShallow((s) => {
      const convo = s.conversations.find((c) => c.id === convoId);
      return convo ? convo.messages : [];
    }),
  );

  return {
    messages,
    send: (body) => messagesService.send(convoId, body),
  };
}

export interface ConversationInvitesHook {
  /** The caller's own pending group invites, newest first. */
  invites: ConversationInviteSummary[];
  /** True until the current listInvites() attempt settles. */
  loading: boolean;
  /** True when the last listInvites() attempt failed. */
  error: boolean;
  /** Re-fetch the list from the server (also clears `error`). */
  refresh: () => void;
  /**
   * Accept an invite: the store gains the now-joined conversation and the row
   * drops from `invites`. Returns the conversation so the caller can select it.
   */
  accept: (id: string | number) => Promise<Conversation>;
  /** Decline an invite: the row drops from `invites`. The store is untouched —
   *  a decline is not a conversation on the wire. */
  decline: (id: string | number) => Promise<void>;
}

/**
 * Hook for the groups-view "Invitations" block. Fetches on mount (and again
 * whenever `refresh()` bumps the reload token).
 *
 * `loading` is derived by comparing `settledToken` (which reload attempt last
 * settled) against the current `reloadToken` — the same technique as
 * useUserByHandle above — rather than a separate setState called directly in
 * the effect body (react-hooks/set-state-in-effect). The alive guard +
 * .then/.catch/.finally chain also mirrors useUserByHandle exactly, so an
 * unmount mid-fetch can't set state on a gone component, and a rejection
 * can't leave `loading` stuck true forever.
 *
 * accept/decline are pessimistic (await the server, THEN drop the row) —
 * deliberately not optimistic, so a failed respond leaves the row in place
 * for the caller to retry rather than vanishing and silently reappearing.
 */
export function useConversationInvites(): ConversationInvitesHook {
  const [invites, setInvites] = useState<ConversationInviteSummary[]>([]);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [settledToken, setSettledToken] = useState(-1);

  useEffect(() => {
    let alive = true;
    void messagesService
      .listInvites()
      .then((list) => {
        if (!alive) return;
        setInvites(list);
        setError(false);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setSettledToken(reloadToken);
      });
    return () => {
      alive = false;
    };
  }, [reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  const accept = useCallback(async (id: string | number) => {
    const res = await messagesService.respondInvite(id, true);
    setInvites((cur) => cur.filter((i) => i.id !== id));
    if (res.status !== 'accepted') {
      // Unreachable per the wire contract (accept always returns a conversation) —
      // guarded so a future server change fails loudly instead of returning undefined.
      throw new Error('Expected an accepted invite to return a conversation');
    }
    return res.conversation;
  }, []);

  const decline = useCallback(async (id: string | number) => {
    await messagesService.respondInvite(id, false);
    setInvites((cur) => cur.filter((i) => i.id !== id));
  }, []);

  return { invites, loading: reloadToken !== settledToken, error, refresh, accept, decline };
}
